import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ShoppingBag, User, Mail, Phone, Calendar, CreditCard, FileText } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { FinancialStatusBadge, fmtMoney, fmtDate } from "@/pages/AdminEncomendas";

export default function AdminEncomendaDetail() {
  const { name } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/encomendas/${encodeURIComponent(name)}`);
        if (alive) setItem(data);
      } catch (e) {
        if (alive) setError(formatApiError(e.response?.data?.detail) || e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [name]);

  if (loading) {
    return <div className="text-[var(--ldr-text-muted)]" data-testid="encomenda-loading">A carregar…</div>;
  }
  if (error || !item) {
    return (
      <div className="ldr-card p-6" data-testid="encomenda-error">
        <p className="text-[var(--ldr-red)]">{error || "Encomenda não encontrada."}</p>
        <Link to="/admin/encomendas" className="ldr-btn-outline mt-4 inline-flex">
          <ArrowLeft size={14} /> Voltar
        </Link>
      </div>
    );
  }

  const linked = item.linked_refunds || [];

  return (
    <div data-testid="encomenda-detail">
      <div className="flex items-center justify-between gap-4 mb-6">
        <Link to="/admin/encomendas" className="ldr-btn-outline" data-testid="encomenda-back">
          <ArrowLeft size={14} /> Voltar
        </Link>
        <FinancialStatusBadge status={item.financial_status} />
      </div>

      <header className="mb-8 flex items-start gap-4">
        <div className="w-12 h-12 rounded-md bg-[#FBEAEA] text-[var(--ldr-red)] flex items-center justify-center shrink-0">
          <ShoppingBag size={22} />
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-[var(--ldr-text-muted)]">Encomenda</p>
          <h1 className="mt-1 text-3xl font-bold text-[var(--ldr-text)] tracking-wide" data-testid="encomenda-name">
            {item.name}
          </h1>
          <p className="mt-2 text-sm text-[var(--ldr-text-muted)]">
            {fmtDate(item.order_created_at)}
            {item.shopify_id ? <> · Shopify ID {item.shopify_id}</> : null}
          </p>
        </div>
      </header>

      {linked.length > 0 && (
        <div className="ldr-card p-4 mb-6 border-l-4 !border-l-[var(--ldr-red)]" data-testid="encomenda-linked-refunds">
          <div className="text-xs uppercase tracking-widest text-[var(--ldr-text-muted)] mb-2">
            Pedidos de reembolso associados
          </div>
          <ul className="space-y-2">
            {linked.map((r) => (
              <li key={r.numero_processo} className="flex items-center justify-between gap-4">
                <Link
                  to={`/admin/reembolsos/${r.numero_processo}`}
                  className="font-semibold text-[var(--ldr-red)]"
                  data-testid={`linked-refund-${r.numero_processo}`}
                >
                  {r.numero_processo}
                </Link>
                <span className="text-sm text-[var(--ldr-text-muted)] truncate">
                  {r.nome} · {fmtDate(r.created_at)}
                </span>
                <span className="text-xs font-semibold uppercase tracking-wider text-[var(--ldr-text)]">
                  {r.status}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <section className="lg:col-span-2 ldr-card p-6" data-testid="encomenda-info">
          <h2 className="text-sm font-semibold uppercase tracking-widest text-[var(--ldr-text-muted)] mb-4">
            Cliente & pagamento
          </h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
            <Field icon={<User size={14} />} label="Nome" value={item.customer_name} />
            <Field
              icon={<Mail size={14} />}
              label="Email"
              value={item.email ? <a href={`mailto:${item.email}`}>{item.email}</a> : null}
            />
            <Field
              icon={<Phone size={14} />}
              label="Telefone"
              value={item.phone ? <a href={`tel:${item.phone}`}>{item.phone}</a> : null}
            />
            <Field icon={<Calendar size={14} />} label="Data da compra" value={fmtDate(item.order_created_at)} />
            <Field icon={<Calendar size={14} />} label="Data do pagamento" value={fmtDate(item.paid_at)} />
            <Field icon={<CreditCard size={14} />} label="Método de pagamento" value={item.payment_method} />
            <Field label="Referência de pagamento" value={item.payment_reference} />
            <Field label="Código de desconto" value={item.discount_code} />
          </dl>

          <h2 className="text-sm font-semibold uppercase tracking-widest text-[var(--ldr-text-muted)] mt-8 mb-3">
            Totais
          </h2>
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 text-sm">
            <Money label="Subtotal" value={item.subtotal} currency={item.currency} />
            <Money label="Descontos" value={item.discount_amount} currency={item.currency} />
            <Money label="Portes" value={item.shipping} currency={item.currency} />
            <Money label="Impostos" value={item.tax} currency={item.currency} />
            <Money label="Reembolsado" value={item.refunded_amount} currency={item.currency} />
            <Money label="Saldo em dívida" value={item.outstanding_balance} currency={item.currency} />
          </dl>
          <div className="border-t border-[var(--ldr-border)] mt-4 pt-4 flex items-center justify-between">
            <span className="text-sm font-semibold uppercase tracking-widest text-[var(--ldr-text-muted)]">
              Total pago
            </span>
            <span className="text-2xl font-bold text-[var(--ldr-text)]">
              {fmtMoney(item.total, item.currency)}
            </span>
          </div>

          {(item.billing_address || item.shipping_address) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-8">
              {item.billing_address && (
                <AddressCard title="Morada de faturação" address={item.billing_address} />
              )}
              {item.shipping_address && (
                <AddressCard title="Morada de envio" address={item.shipping_address} />
              )}
            </div>
          )}
        </section>

        <aside className="ldr-card p-6 h-fit" data-testid="encomenda-items">
          <h2 className="text-sm font-semibold uppercase tracking-widest text-[var(--ldr-text-muted)] mb-3 flex items-center gap-2">
            <FileText size={14} /> Itens ({item.total_items_count || (item.items || []).length})
          </h2>
          {(!item.items || item.items.length === 0) ? (
            <p className="text-sm text-[var(--ldr-text-muted)]">Sem itens registados.</p>
          ) : (
            <ul className="space-y-3">
              {item.items.map((it, i) => (
                <li
                  key={i}
                  className="border border-[var(--ldr-border)] rounded-md p-3 bg-[var(--ldr-bg-alt)]"
                  data-testid={`encomenda-item-${i}`}
                >
                  <div className="font-medium text-sm">{it.product}</div>
                  {it.variant && (
                    <div className="text-xs text-[var(--ldr-text-muted)] mt-0.5">{it.variant}</div>
                  )}
                  {it.sku && (
                    <div className="text-xs font-mono text-[var(--ldr-text-muted)]">SKU: {it.sku}</div>
                  )}
                  <div className="flex items-center justify-between mt-2 text-sm">
                    <span className="text-[var(--ldr-text-muted)]">
                      {it.quantity ?? 1} × {fmtMoney(it.unit_price, item.currency)}
                    </span>
                    <span className="font-semibold">
                      {fmtMoney(it.total, item.currency)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </aside>
      </div>
    </div>
  );
}

function Field({ icon, label, value }) {
  const display = value == null || value === "" ? "—" : value;
  return (
    <div>
      <dt className="text-xs uppercase tracking-widest text-[var(--ldr-text-muted)] flex items-center gap-1.5">
        {icon}
        {label}
      </dt>
      <dd className="mt-1 text-[var(--ldr-text)] break-words">{display}</dd>
    </div>
  );
}

function Money({ label, value, currency }) {
  if (value == null) return null;
  return (
    <div>
      <dt className="text-xs uppercase tracking-widest text-[var(--ldr-text-muted)]">{label}</dt>
      <dd className="text-[var(--ldr-text)] font-medium">{fmtMoney(value, currency)}</dd>
    </div>
  );
}

function AddressCard({ title, address }) {
  const lines = [
    address.name,
    address.company,
    address.address1 || address.street,
    address.address2,
    [address.zip, address.city].filter(Boolean).join(" "),
    address.province_name || address.province,
    address.country,
    address.phone,
  ].filter(Boolean);
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-[var(--ldr-text-muted)] mb-2">
        {title}
      </div>
      <address className="not-italic text-sm text-[var(--ldr-text)] leading-relaxed">
        {lines.map((l, i) => (
          <div key={i}>{l}</div>
        ))}
      </address>
    </div>
  );
}
